import axios from 'axios'
import { config } from '../config'
import { toAuthError } from './authErrors'

export class AuthClient {
	#accessToken = null
	#refreshPromise = null
	#sessionExpiredHandler = null

	constructor(http) {
		this.http = http
	}

	getAccessToken() {
		return this.#accessToken
	}

	setSessionExpiredHandler(handler) {
		this.#sessionExpiredHandler = handler
	}

	clearAccessToken() {
		this.#accessToken = null
	}

	async register(email, password) {
		try {
			return (await this.http.post('/auth/register', { email, password })).data
		} catch (error) {
			throw toAuthError(error)
		}
	}

	async login(email, password) {
		try {
			const response = await this.http.post('/auth/login', { email, password, clientType: 'web' })
			this.#setAccessToken(response.data.accessToken)
			return this.getCurrentUser()
		} catch (error) {
			this.clearAccessToken()
			throw toAuthError(error)
		}
	}

	async bootstrap() {
		try {
			await this.refresh()
			return await this.getCurrentUser()
		} catch {
			this.clearAccessToken()
			return null
		}
	}

	refresh() {
		if (this.#refreshPromise) return this.#refreshPromise
		this.#refreshPromise = this.http.post('/auth/refresh')
			.then(response => {
				this.#setAccessToken(response.data.accessToken)
				return response.data.accessToken
			})
			.catch(error => {
				this.clearAccessToken()
				throw toAuthError(error)
			})
			.finally(() => {
				this.#refreshPromise = null
			})
		return this.#refreshPromise
	}

	async getCurrentUser() {
		try {
			const response = await this.http.get('/auth/me', {
				headers: { Authorization: `Bearer ${this.#accessToken}` },
			})
			return response.data
		} catch (error) {
			throw toAuthError(error)
		}
	}

	async logout() {
		this.clearAccessToken()
		try {
			await this.http.post('/auth/logout')
		} catch (error) {
			if (error?.response?.status !== 401) throw toAuthError(error)
		}
	}

	async verifyEmail(token) {
		try { return (await this.http.post('/auth/verify-email', { token })).data }
		catch (error) { throw toAuthError(error) }
	}

	async resendVerification(email) {
		try { return (await this.http.post('/auth/resend-verification', { email })).data }
		catch (error) { throw toAuthError(error) }
	}

	async forgotPassword(email) {
		try { return (await this.http.post('/auth/forgot-password', { email })).data }
		catch (error) { throw toAuthError(error) }
	}

	async resetPassword(token, newPassword) {
		try { return (await this.http.post('/auth/reset-password', { token, newPassword })).data }
		catch (error) { throw toAuthError(error) }
	}

	notifySessionExpired() {
		this.clearAccessToken()
		this.#sessionExpiredHandler?.()
	}

	#setAccessToken(token) {
		if (!token || typeof token !== 'string') throw new Error('Missing access token')
		this.#accessToken = token
	}
}

const authHttp = axios.create({ baseURL: config.apiUrl, withCredentials: true })
const authClient = new AuthClient(authHttp)
export default authClient
